import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Stack, Switch,
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, CircularProgress, Alert, Tooltip,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Repeat as RepeatIcon,
  PhoneAndroid as PushIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import RoundupEditor from './RoundupEditor';
import CommEmptyState from './shared/CommEmptyState';
import { commTabHeaderSx, commTableHeadRowSx, CATEGORY_META } from './shared/communicationsStyles';

interface Roundup {
  roundup_id: string;
  name: string;
  enabled: boolean;
  scope_type: string;
  email_template_id: string | null;
  schedule_id: string;
  schedule_name: string;
  alert_level_id: string | null;
  alert_type: string | null;
  minutes_after_scheduled_time: number | null;
  template_name: string | null;
  scope_location_name: string | null;
  recipient_count: number;
  last_sent_at: string | null;
  send_push_notification: boolean;
  check_category?: string | null;
  standalone_recurrence_type?: string | null;
  standalone_days_of_week?: number[];
  standalone_time_of_day?: string | null;
}

const SCOPE_LABELS: Record<string, string> = {
  all: 'All Assets',
  location: 'By Location',
  asset_type: 'By Type',
  asset_list: 'Specific Assets',
  asset_tag: 'By Tag',
  template: 'By Template',
  asset_template: 'Asset + Template',
};

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function describeRoundupRecurrence(r: Roundup): string {
  const rt = r.standalone_recurrence_type ?? 'weekly';
  if (rt === 'daily') return 'Daily';
  if (rt === 'weekly') {
    const days = (r.standalone_days_of_week ?? []).map(d => DAY_SHORT[d]).join(', ');
    return days || 'Weekly';
  }
  return rt.replace(/_/g, ' ');
}

const RoundupsTab: React.FC = () => {
  const token = localStorage.getItem('token');

  const [roundups, setRoundups] = useState<Roundup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<Roundup | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Roundup | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  // Business feature flags for location scoping
  const [bizLocationsEnabled, setBizLocationsEnabled] = useState(true);
  const [bizLocationLabel, setBizLocationLabel] = useState('Location');

  useEffect(() => {
    const bid = localStorage.getItem('businessId');
    if (!bid || !token) return;
    axios
      .get(`${API_BASE_URL}/businesses/${bid}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setBizLocationsEnabled(res.data.allow_locations !== 0 && res.data.allow_locations !== false);
        setBizLocationLabel(
          res.data.use_site_terminology === 1 || res.data.use_site_terminology === true ? 'Site' : 'Location'
        );
      })
      .catch(() => {/* non-fatal — keep defaults */});
  }, [token]);

  const fetchRoundups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/communication-roundups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoundups(res.data.data ?? []);
    } catch {
      setError('Failed to load round-ups.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchRoundups(); }, [fetchRoundups]);

  const handleToggle = async (roundup: Roundup) => {
    try {
      const res = await axios.patch(
        `${API_BASE_URL}/communication-roundups/${roundup.roundup_id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoundups((prev) =>
        prev.map((r) => r.roundup_id === roundup.roundup_id ? { ...r, enabled: res.data.enabled } : r)
      );
    } catch {
      setSnackbar({ open: true, message: 'Failed to toggle round-up.', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/communication-roundups/${deleteTarget.roundup_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteTarget(null);
      fetchRoundups();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete round-up.', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleTest = async (roundup: Roundup) => {
    setTestingId(roundup.roundup_id);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/communication-roundups/${roundup.roundup_id}/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: res.data?.message || 'Test round-up email sent to your account email.', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to send test.', severity: 'error' });
    } finally {
      setTestingId(null);
    }
  };

  const handleCreate = () => {
    setEditing(null);
    setIsNew(true);
    setEditorOpen(true);
  };

  const handleEdit = (roundup: Roundup) => {
    setEditing(roundup);
    setIsNew(false);
    setEditorOpen(true);
  };

  const handleSave = async (data: any) => {
    if (isNew) {
      await axios.post(`${API_BASE_URL}/communication-roundups`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      await axios.put(`${API_BASE_URL}/communication-roundups/${editing!.roundup_id}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }
    setEditorOpen(false);
    fetchRoundups();
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={commTabHeaderSx}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Round-Up Emails</Typography>
          <Typography variant="body2" color="text.secondary">
            Standalone round-ups fire on their own recurring schedule (UK time). Configure name, time, look-back,
            asset scope, and recipients here. Schedule-linked summaries are set under{' '}
            <strong>Schedules</strong>.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Create Round-Up
        </Button>
      </Box>

      {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && roundups.length === 0 && (
        <CommEmptyState
          icon={<RepeatIcon />}
          heading="No round-ups configured yet"
          description="Create a standalone round-up to start sending scheduled summary emails to your team."
          ctaLabel="Create Round-Up"
          onCta={handleCreate}
        />
      )}

      <RoundupEditor
        open={editorOpen}
        roundup={editing}
        isNew={isNew}
        onSave={handleSave}
        onClose={() => setEditorOpen(false)}
        locationsEnabled={bizLocationsEnabled}
        locationLabel={bizLocationLabel}
      />

      {!loading && roundups.length > 0 && (
        <Paper>
          <Table>
            <TableHead>
              <TableRow sx={commTableHeadRowSx}>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell align="center">Recipients</TableCell>
                <TableCell>Last Sent</TableCell>
                <TableCell align="center">Enabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roundups.map((r) => (
                <TableRow key={r.roundup_id} hover sx={{ opacity: r.enabled ? 1 : 0.6 }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                    {r.schedule_name && (
                      <Typography variant="caption" color="text.secondary">{r.schedule_name}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const cat = r.check_category ?? 'all';
                      if (cat === 'all') {
                        return <Chip label="All Checks" size="small" variant="outlined" />;
                      }
                      const meta = CATEGORY_META[cat as keyof typeof CATEGORY_META];
                      if (!meta) return <Chip label={cat} size="small" variant="outlined" />;
                      return (
                        <Chip
                          label={meta.label}
                          size="small"
                          sx={{ bgcolor: meta.bg, color: meta.color, border: `1px solid ${meta.border}`, fontWeight: 600 }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{describeRoundupRecurrence(r)}</Typography>
                    {r.standalone_time_of_day && (
                      <Typography variant="caption" color="text.secondary">
                        {r.standalone_time_of_day.slice(0, 5)} · Europe/London
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={SCOPE_LABELS[r.scope_type] || r.scope_type}
                      size="small"
                      variant="outlined"
                    />
                    {r.scope_location_name && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {r.scope_location_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography variant="body2">
                        {r.template_name ?? <em style={{ color: '#9ca3af' }}>System default</em>}
                      </Typography>
                      {r.send_push_notification && (
                        <Tooltip title="Push notification enabled">
                          <PushIcon fontSize="small" color="primary" sx={{ opacity: 0.8 }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={r.recipient_count} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {r.last_sent_at
                        ? new Date(r.last_sent_at).toLocaleString('en-GB')
                        : <em>Never</em>}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={!!r.enabled}
                      onChange={() => handleToggle(r)}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                    <Stack spacing={0.75} alignItems="flex-end">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(r)} disabled={testingId !== null}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(r)} disabled={testingId !== null}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Tooltip title="Send a sample round-up email to your account email">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            fullWidth
                            startIcon={
                              testingId === r.roundup_id
                                ? <CircularProgress size={14} color="inherit" />
                                : <SendIcon sx={{ fontSize: 16 }} />
                            }
                            onClick={() => handleTest(r)}
                            disabled={testingId !== null}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 100, maxWidth: 120 }}
                          >
                            {testingId === r.roundup_id ? 'Sending…' : 'Test email'}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete round-up?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoundupsTab;
